import React, { useState } from 'react';
import { Outlet, Link, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { LayoutDashboard, Users, Briefcase, DollarSign, LogOut, Scale, Shield, Calendar, MessageSquare, FileText, Bell, CalendarDays, Menu, X } from 'lucide-react';
import NotificationManager from './NotificationManager';
import ChatWidget from './ChatWidget';

const Layout = () => {
  const { logout, usuario } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [sidebarOpen, setSidebarOpen] = useState(false);

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const closeSidebar = () => {
    setSidebarOpen(false);
  };

  const isAdmin = usuario?.permissoes?.admin || false;
  const canViewClientes = usuario?.permissoes?.ver_clientes || isAdmin;
  const canViewProcessos = usuario?.permissoes?.ver_processos || isAdmin;
  const canViewAudiencias = usuario?.permissoes?.ver_audiencias || isAdmin;
  const canViewAtendimentos = usuario?.permissoes?.ver_atendimentos || isAdmin;
  const canViewOrcamentos = usuario?.permissoes?.ver_orcamentos || isAdmin;
  const canViewAnotacoes = usuario?.permissoes?.ver_anotacoes || isAdmin;
  const canViewFinanceiro = usuario?.permissoes?.ver_financeiro || isAdmin;

  const menuItems = [
    { path: '/', icon: LayoutDashboard, label: 'Dashboard', show: true },
    { path: '/atendimentos', icon: MessageSquare, label: 'Atendimentos', show: canViewAtendimentos },
    { path: '/calendario', icon: CalendarDays, label: 'Calendário', show: true },
    { path: '/clientes', icon: Users, label: 'Clientes', show: canViewClientes },
    { path: '/processos', icon: Briefcase, label: 'Processos', show: canViewProcessos },
    { path: '/audiencias', icon: Calendar, label: 'Audiências', show: canViewAudiencias },
    { path: '/orcamentos', icon: FileText, label: 'Orçamento', show: canViewOrcamentos },
    { path: '/financeiro', icon: DollarSign, label: 'Financeiro', show: canViewFinanceiro },
    { path: '/anotacoes', icon: Bell, label: 'Anotações', show: canViewAnotacoes },
    { path: '/admin', icon: Shield, label: 'Administração', show: isAdmin }
  ];

  const isActive = (path) => {
    if (path === '/') return location.pathname === '/';
    return location.pathname.startsWith(path);
  };

  return (
    <div className="min-h-screen bg-slate-50" data-testid="layout">
      {/* Mobile Header */}
      <header className="lg:hidden fixed top-0 left-0 right-0 h-16 bg-white border-b border-slate-200 flex items-center justify-between px-4 z-40">
        <div className="flex items-center gap-3">
          <div className="w-8 h-8 bg-slate-900 rounded-lg flex items-center justify-center">
            <Scale className="w-5 h-5 text-white" />
          </div>
          <h1 className="font-semibold text-slate-900 text-sm">Advocacia Fulini</h1>
        </div>
        <div className="flex items-center gap-2">
          <NotificationManager />
          <Button
            variant="ghost"
            size="sm"
            onClick={() => setSidebarOpen(!sidebarOpen)}
            data-testid="mobile-menu-button"
          >
            {sidebarOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </Button>
        </div>
      </header>

      {/* Overlay for mobile */}
      {sidebarOpen && (
        <div 
          className="lg:hidden fixed inset-0 bg-black/50 z-40"
          onClick={closeSidebar}
        />
      )}

      {/* Sidebar */}
      <aside 
        className={`fixed left-0 top-0 h-full w-64 bg-white border-r border-slate-200 flex flex-col z-50 transition-transform duration-300 ease-in-out
          ${sidebarOpen ? 'translate-x-0' : '-translate-x-full'}
          lg:translate-x-0
        `}
        data-testid="sidebar"
      >
        {/* Logo - Hidden on mobile (shown in header) */}
        <div className="p-6 border-b border-slate-200 hidden lg:block">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-slate-900 rounded-lg flex items-center justify-center">
              <Scale className="w-6 h-6 text-white" />
            </div>
            <div>
              <h1 className="font-semibold text-slate-900">Advocacia Fulini</h1>
            </div>
          </div>
        </div>

        {/* Mobile close button area */}
        <div className="lg:hidden p-4 border-b border-slate-200 flex justify-between items-center">
          <span className="font-semibold text-slate-900">Menu</span>
          <Button variant="ghost" size="sm" onClick={closeSidebar}>
            <X className="w-5 h-5" />
          </Button>
        </div>

        <nav className="flex-1 p-4 space-y-1 overflow-y-auto" data-testid="navigation">
          {menuItems.filter(item => item.show).map((item) => {
            const Icon = item.icon;
            return (
              <Link
                key={item.path}
                to={item.path}
                onClick={closeSidebar}
                className={`flex items-center gap-3 px-4 py-3 rounded-lg transition-colors ${
                  isActive(item.path)
                    ? 'bg-slate-900 text-white'
                    : 'text-slate-600 hover:bg-slate-100'
                }`}
                data-testid={`nav-link-${item.label.toLowerCase()}`}
              >
                <Icon className="w-5 h-5" />
                <span className="font-medium">{item.label}</span>
              </Link>
            );
          })}
        </nav>

        <div className="p-4 border-t border-slate-200">
          <div className="mb-3 px-4 flex justify-between items-center">
            <div className="min-w-0 flex-1">
              <p className="text-sm font-medium text-slate-900 truncate">{usuario?.nome}</p>
              <p className="text-xs text-slate-600 truncate">{usuario?.email}</p>
            </div>
            <div className="hidden lg:block">
              <NotificationManager />
            </div>
          </div>
          <Button
            variant="outline"
            className="w-full justify-start"
            onClick={handleLogout}
            data-testid="logout-button"
          >
            <LogOut className="w-4 h-4 mr-2" />
            Sair
          </Button>
        </div>

        {/* Footer */}
        <div className="p-3 border-t border-slate-200 text-center">
          <p className="text-xs text-slate-500">
            Desenvolvido por <span className="font-medium text-slate-700">Danilo Pasoto</span>
          </p>
        </div>
      </aside>

      {/* Main Content */}
      <main className="lg:ml-64 min-h-screen pt-16 lg:pt-0 pb-12 lg:pb-0">
        <Outlet />
      </main>

      {/* Chat Widget */}
      <ChatWidget />

      {/* Mobile Footer */}
      <footer className="lg:hidden fixed bottom-0 left-0 right-0 bg-white border-t border-slate-200 py-2 text-center z-30">
        <p className="text-xs text-slate-500">
          Desenvolvido por <span className="font-medium text-slate-700">Danilo Pasoto</span>
        </p>
      </footer>
    </div>
  );
};

export default Layout;
